import { RoleEnum } from './invitation.types';
import { ProfileType } from './profile';
import { RichContentData } from './richContent';

export interface WelcomeSlugType {
  slug: string;
}

export enum WelcomeContentEnum {
  HOME_STUDY = 'home-study',
  DETAILED_HOME_STUDY = 'detailed-home-study',
  ONBOARDING_STUDY = 'onboarding-study',
  INELIGIBLE_PAGE = 'ineligible-page',
  // The CONSENT content a researcher flagged as the study's public information
  // notice (« note d'information publique ») — a reference document offered on the
  // landing, NOT the consent the visitor signs (that one comes at the end of signup).
  PUBLIC_NOTICE = 'public-notice',
}

export interface WelcomeType {
  id: number;
  contents: {
    [WelcomeContentEnum.HOME_STUDY]: WelcomeContentType
    [WelcomeContentEnum.DETAILED_HOME_STUDY]: WelcomeContentType
    // id is null when the study has no onboarding page configured in Diapason.
    [WelcomeContentEnum.ONBOARDING_STUDY]: { id: number | null }
    // OPTIONAL on purpose: the key is absent from a welcome payload cached before this
    // feature shipped, and from an older API build. Every read must be optional-chained
    // so the landing degrades to "no link" instead of crashing.
    [WelcomeContentEnum.PUBLIC_NOTICE]?: { id: number | null }
  },
  invitations: Invitations,
  roles: ProfileType[]
}

export interface Invitations {
  from_user: invitUser,
  to_user: invitUser
}

export interface invitUser {
  id: number,
  firstname: string,
  lastname: string,
  email: string,
  phone_number: string,
  external_id: string,
  user_status: UserStatus,
  role: RoleEnum
  profile: number
}


export enum UserStatus {
  PROSPECT = "PROSPECT",
  INCLUSION_EN_ATTENTE_DE_VALIDATION = "INCLUSION_EN_ATTENTE_DE_VALIDATION",
  INCLUSION_VALIDEE = "INCLUSION_VALIDEE",
  ACTIVE = "ACTIVE",
}

export enum RoleKeyEnum {
  INVESTIGATOR = 'investigator',
  INTERVENORS = 'intervenor',
  SUBJECT = 'subject',
}

export interface WelcomeRoleType {
  [RoleKeyEnum.INVESTIGATOR]: ProfileType[],
  [RoleKeyEnum.INTERVENORS]: ProfileType[],
  // Subjects are now multi-role (father/mother/enfant…), like investigators/intervenors.
  [RoleKeyEnum.SUBJECT]: ProfileType[],
}

export interface WelcomeContentType {
  id: number,
  content: RichContentData,
  avg_amount_time_to_read: number,
  // JSON string with page presentation (background …); null/absent = app default
  page_style?: string | null
}

// Researcher-authored checkbox item of a CONSENT content. `label` is already resolved
// for the requested lang (primary-language fallback) by the API.
export interface ConsentCheckboxItemType {
  id: number,
  order: number,
  is_required: boolean,
  export_code: string | null,
  version: number,
  label: string,
  // Consent option this item grants (null/absent = plain checkbox). An OPTIONAL linked
  // item renders as an explicit « J'accepte / Je refuse » choice (ConsentCheckboxList).
  consent_option_id?: number | null,
}

export interface StudyContentType extends WelcomeContentType {
  type: string,
  label: string,
  version: number,
  // CONSENT contents only — [] (or absent) means the single generic checkbox applies.
  consent_checkbox_items?: ConsentCheckboxItemType[]
}

/**
 * La note d'information publique telle qu'elle arrive à un appelant ANONYME.
 * Volontairement plus étroite que StudyContentType : l'API sert une allowlist
 * (services/contents/publicNotice.ts getPublicNotice — id, type, content,
 * avg_amount_time_to_read, page_style) et retire tout le reste, dont `label`
 * (le libellé d'administration Diapason) et `version`, que StudyContentType
 * déclare OBLIGATOIRES. Ces champs doivent donc rester HORS de ce type : sinon
 * un futur `notice.label` en titre ou un `notice.version` en tampon compilerait
 * sans erreur et afficherait « undefined » en production.
 */
export type PublicNoticeType = Pick<
  StudyContentType,
  'id' | 'type' | 'content' | 'avg_amount_time_to_read' | 'page_style'
>

export enum WelcompeStepEnum {
  HOME = 'HOME',
  HOME_DETAILS = 'HOME_DETAILS',
  PROFILE_SELECTOR = 'PROFILE_SELECTOR',
  HAS_ACCOUNT = 'HAS_ACCOUNT',
  LOGIN = 'LOGIN',
  ON_BOARDING = 'ON_BOARDING',
  CONSENT = 'CONSENT',
  INCLUSION = 'INCLUSION',
  INSCRIPTION = 'INSCRIPTION',
  // Dedicated NIR screen (SNDS appariement), right after the OTP-verified
  // inscription step — only for roles with nir_collection_mode ≠ HIDDEN.
  NIR = 'NIR',
  // Family composition: obligatory "how many <data-only role>?" question, right after
  // the inscription step (the numéro de dossier is needed for the skip pre-check).
  FAMILY_COMPOSITION = 'FAMILY_COMPOSITION',
  STRATIFICATION = 'STRATIFICATION',
  SITES = 'SITES',
  NOT_INCLUDED = 'NOT_INCLUDED',
  REGISTRED_SUCCESSFULLY = 'REGISTRED_SUCCESSFULLY',
  INVITATION = 'INVITATION',
  INIT_IDENTIFICATION = 'INIT_IDENTIFICATION',
}

export interface InclusionAnswerType {
  id: number,
  version: number,
  answer: boolean | '',
}

export interface PostUserType {
  role_id: number,
  invitation_token?: string,
  lang: string,
}

export interface PatchUserType {
  firstname: string,
  lastname: string,
  email: string,
  phone_number: string,
  external_id: string,
  nickname: string,
  // Precision-trimmed ISO birthdate (deterministic profiling). Empty when the role doesn't collect it.
  birthdate?: string,
  // Home address (free text). Empty when the role doesn't collect it.
  address?: string,
}

export interface PostUserGamification {
  points: number,
  step: number,
  animation_success: number
}

export interface UserGamificationDetails extends PostUserGamification {
  pointsEarned: number,
  stepEarned: number,
  animation_successEarned: number,
}

export interface PostUserResponseType {
  token: string,
  user_id: number,
}

export interface LoginResponseType {
  token: string,
}

export type LoginType = PasswordSignInType | EmailLoginType | NicknameLoginType


export interface PasswordSignInType {
  "PASSWORD-SIGNIN": {
    login: string,
    password: string,
  }
}

export interface EmailLoginType {
  "LOGIN-PASSWORD": {
    login: string,
    password: string,
  }
}

export interface NicknameLoginType {
  "NICKNAME-PASSWORD": {
    nickname: string,
    password: string,
  }
}

export interface StratQuestionType {
  id: number,
  version: number,
  export_code: string,
  label: string,
  order: number,
  answers: StratAnswerType[]
}

export interface StratAnswerType {
  id: number,
  version: number,
  export_code: string,
  label: string,
  order: number,
}

export interface SiteType {
  id: number,
  label: string,
  description: string,
  address: string,
  subsites: SubsiteType[],
}

export interface SubsiteType {
  id: number,
  label: string
}

export interface PostInclusionQuestionType {
  is_eligible: boolean
  not_eligible_content_id?: number
}

export interface PostSiteType {
  sites: {
    site_id: number;
    subsites: number[];
  }[]
}

export interface postConsentRequestType {
  "user": {
    "firstname": string;
    "lastname": string;
    "email": string;
    "nickname": string;
    "password": string;
    "phone_number": string;
    "external_id": string;
    "birthdate"?: string;
    "address"?: string;
  },
  "consent": {
    "id": string;
    "is_validated": string;
    "validation_date": string;
    // One entry per active checkbox item of the consent content;
    // an unchecked OPTIONAL item is sent as is_accepted: false (recorded refusal).
    // version echoes the rendered item version (API rejects a mid-signup mismatch).
    "items"?: { id: number; is_accepted: boolean; version: number }[];
    // true = every option-linked OPTIONAL item was an explicit « J'accepte / Je refuse »
    // choice, so the API may record is_accepted:false on a linked item as a REFUSED grant.
    // Absent (older builds) ⇒ a false item never becomes a refusal.
    "explicit_choices"?: boolean;
  },
  "identification_questions"?: {
    "id": number;
    "version": number;
    "answer": string;
  }[];
  "stratification_questions"?: {
    "question_id": number;
    "question_version": number,
    "answer_id": number,
    "answer_version": number
  }[]
  // Family composition declared at signup (data-only roles with auto_create_enabled):
  // the rows are created server-side at consent, bounded + deduped there.
  "family_composition"?: {
    "study_role_id": number;
    "count": number;
  }[]
  // Absence declarations (« pas de second parent ») for the roles this registrant may
  // declare (absence_declarable_by_study_role_id); written inside the consent transaction
  // as dossier_role_declarations rows. is_absent:false lifts a stale declaration.
  "role_absences"?: {
    "study_role_id": number;
    "is_absent": boolean;
  }[]
}
